import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { useGetHistoryQuery } from '../api/apiSlice';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { HistoryItem } from '../types/api';
import { sportMeta } from '../constants/sportMeta';
import { groupByMonth } from '../utils/dateGrouping';

import StatTile from './StatTile';
import FeedCard from './FeedCard';
import FeedCardSkeleton from './FeedCardSkeleton';
import DayGroupHeader from './DayGroupHeader';

type KindFilter = 'all' | 'outing' | 'event';

function History() {
    const { t, i18n } = useTranslation();
    const sportTypeLabel = useSportTypeLabel();
    const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';

    const { data, isLoading } = useGetHistoryQuery();
    const items = useMemo(() => data?.items ?? [], [data]);
    const stats = data?.stats;

    const [kind, setKind] = useState<KindFilter>('all');
    const [year, setYear] = useState<number | 'all'>('all');

    const years = useMemo(() => {
        const set = new Set<number>();
        items.forEach((item) => {
            if (item.date) set.add(new Date(item.date).getFullYear());
        });
        return Array.from(set).sort((a, b) => b - a);
    }, [items]);

    const filtered = useMemo(
        () =>
            items.filter((item) => {
                if (kind !== 'all' && item.kind !== kind) return false;
                if (year !== 'all' && (!item.date || new Date(item.date).getFullYear() !== year)) return false;
                return true;
            }),
        [items, kind, year],
    );

    const groups = useMemo(
        () => groupByMonth(filtered, (item) => (item.date ? new Date(item.date) : null), locale),
        [filtered, locale],
    );

    const renderCard = (item: HistoryItem) => {
        const meta = sportMeta(item.sportType);
        const cardStats =
            item.kind === 'outing'
                ? [
                      item.sportType ? sportTypeLabel(item.sportType) : null,
                      item.distance != null ? `${Number(item.distance)} ${meta.distanceUnit}` : null,
                      meta.showClimb && item.climb != null ? `${Number(item.climb)} m D+` : null,
                  ].filter((s): s is string => Boolean(s))
                : [t('history.eventTag')];

        if (item.participantCount > 0) {
            cardStats.push(t('feed.participantCount', { count: item.participantCount }));
        }

        return (
            <Grid item key={`${item.kind}-${item.id}`} xs={12} sm={6} md={4}>
                <FeedCard
                    image={item.image}
                    clubName={item.clubName}
                    clubLogo={item.clubLogo}
                    sportType={item.sportType}
                    date={item.date}
                    title={item.title}
                    stats={cardStats}
                />
            </Grid>
        );
    };

    const tilesData: [string, string, boolean][] = stats
        ? [
              [String(stats.totalActivities), t('history.stats.activities'), false],
              [String(stats.totalDistance), t('history.stats.distance'), false],
              [String(stats.totalClimb), t('history.stats.climb'), false],
              [String(stats.thisYear), t('history.stats.thisYear'), false],
              [stats.topSport ? sportTypeLabel(stats.topSport) : t('history.stats.none'), t('history.stats.topSport'), true],
              [String(stats.clubs), t('history.stats.clubs'), false],
          ]
        : [];

    const maxSport = stats ? Math.max(1, ...stats.bySport.map((s) => s.count)) : 1;

    return (
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'left' }}>
            <Typography variant="h4" gutterBottom>
                {t('history.title')}
            </Typography>

            {stats && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 2, md: 3 },
                        alignItems: 'stretch',
                        mb: 3,
                    }}
                >
                    {stats.bySport.length > 0 && (
                        <Box
                            sx={{
                                width: { xs: '100%', md: 300 },
                                flexShrink: 0,
                                order: { xs: 2, md: 0 },
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 3,
                                p: 2,
                            }}
                        >
                            <Typography
                                component="p"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: 'text.secondary',
                                    mb: 1.25,
                                }}
                            >
                                {t('history.bySport')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(64px, max-content) 1fr 2.5ch',
                                    columnGap: 1,
                                    rowGap: 1,
                                    alignItems: 'center',
                                }}
                            >
                                {stats.bySport.map((row) => (
                                    <React.Fragment key={row.type}>
                                        <Box sx={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {sportTypeLabel(row.type)}
                                        </Box>
                                        <Box sx={{ height: 10, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                            <Box
                                                sx={{
                                                    width: `${Math.max(4, (row.count / maxSport) * 100)}%`,
                                                    height: '100%',
                                                    borderRadius: 999,
                                                    bgcolor: sportMeta(row.type).color,
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'text.secondary', textAlign: 'right' }}>
                                            {row.count}
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            order: { xs: 1, md: 0 },
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                        }}
                    >
                        {tilesData.map(([value, label, compact]) => (
                            <StatTile key={label} value={value} label={label} compact={compact} />
                        ))}
                    </Box>
                </Box>
            )}

            {!isLoading && items.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={kind}
                        onChange={(_e, v) => v && setKind(v)}
                    >
                        <ToggleButton value="all">{t('history.filter.all')}</ToggleButton>
                        <ToggleButton value="outing">{t('history.filter.outings')}</ToggleButton>
                        <ToggleButton value="event">{t('history.filter.events')}</ToggleButton>
                    </ToggleButtonGroup>

                    {years.length > 0 && (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={year}
                            onChange={(_e, v) => v != null && setYear(v)}
                        >
                            <ToggleButton value="all">{t('history.filter.allYears')}</ToggleButton>
                            {years.map((y) => (
                                <ToggleButton key={y} value={y}>
                                    {y}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )}
                </Box>
            )}

            {isLoading && (
                <Grid container spacing={2}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <FeedCardSkeleton />
                        </Grid>
                    ))}
                </Grid>
            )}

            {!isLoading && items.length === 0 && (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>
                    {t('history.empty')}
                </Typography>
            )}

            {!isLoading && items.length > 0 && filtered.length === 0 && (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>
                    {t('history.filterEmpty')}
                </Typography>
            )}

            {groups.map((group) => (
                <div key={group.key}>
                    <DayGroupHeader label={group.label} count={group.items.length} />
                    <Grid container spacing={2}>
                        {group.items.map(renderCard)}
                    </Grid>
                </div>
            ))}
        </Container>
    );
}

export default History;
